import { useId, useState } from "react";
import { useLocation } from "react-router";
import { Button } from "@/components/ui/button";
import { Note } from "@/components/ui/Note";
import { Spinner } from "@/components/ui/Spinner";
import { Textarea } from "@/components/ui/textarea";
import { detectEnvironment } from "@/lib/support/detect-environment";
import type {
  Feedback,
  FeedbackKind,
  SubmitResult,
  SupportClient,
} from "@/lib/support/support-client";
import { lookUpReporter } from "@/services/support-reporter";

/**
 * What the form says when no ticket came back. A send that was given up on may
 * still make one, so it is not called a failure: sending it again may file the
 * same feedback twice, and the attorney is told so before they choose.
 */
const NOT_SENT = {
  failed: "Your feedback could not be sent. Please try again.",
  unavailable: "Feedback cannot be sent right now. Please try again later.",
  timed_out:
    "We could not confirm that your feedback was sent. It may still arrive, so sending it again could file it twice.",
} as const;

/**
 * One send at a time. A result is a view to show, never a throw, so the form
 * can stay open with the words kept when no ticket came back. Whether a send
 * is in flight belongs to the popover, which will not close until it settles.
 *
 * The sender's HubSpot contact is looked up inside the send, so it is as
 * fresh as the ticket and the popover's hold covers the wait for it.
 */
function useSend(
  support: SupportClient,
  onSending: (sending: boolean) => void,
) {
  const [notSent, setNotSent] = useState<keyof typeof NOT_SENT | null>(null);

  const send = async (feedback: Feedback) => {
    onSending(true);
    setNotSent(null);
    // Neither the lookup nor the port throws, but a popover held by a send
    // that did would have no way out, so a throw is taken as a failure and
    // the hold always lifts.
    const result: SubmitResult = await lookUpReporter()
      .then((reporter) => support.submit(feedback, reporter))
      .catch(() => ({ ok: false, reason: "failed" }) as const)
      .finally(() => {
        onSending(false);
      });
    if (result.ok) return true;
    setNotSent(result.reason);
    return false;
  };

  return { notSent, send };
}

/**
 * One kind's form, under the popover's heading: the text alone, required for
 * every kind. The browser and the OS go in the message from the browser's own
 * guess. The screen is the path alone, so no query string or fragment goes
 * with it.
 *
 * While a send is in flight the text box is read-only rather than disabled:
 * the ticket carries what it held when Confirm was pressed, and read-only
 * keeps it in the tab order where disabled would drop the keyboard to the page.
 */
export function FeedbackForm({
  kind,
  placeholder,
  support,
  sending,
  onSending,
  onCancel,
  onSent,
}: {
  kind: FeedbackKind;
  placeholder: string;
  support: SupportClient;
  sending: boolean;
  onSending: (sending: boolean) => void;
  onCancel: () => void;
  onSent: () => void;
}) {
  const { pathname } = useLocation();
  const [text, setText] = useState("");
  // Sent in the ticket's header lines. PostHog also records both on the
  // session, so the attorney is not asked for them.
  const [where] = useState(detectEnvironment);
  const { notSent, send } = useSend(support, onSending);
  const noticeId = useId();

  const ready = text.trim() !== "";

  const confirm = async () => {
    if (!ready || sending) return;
    const feedback = { kind, text, ...where, screen: pathname };
    if (await send(feedback)) onSent();
  };

  return (
    <form
      noValidate
      aria-busy={sending}
      onSubmit={(event) => {
        event.preventDefault();
        void confirm();
      }}
      className="px-4 pt-3.5 pb-4"
    >
      <Textarea
        autoFocus
        size="snug"
        required
        aria-label="Your feedback"
        aria-describedby={noticeId}
        placeholder={placeholder}
        readOnly={sending}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
        }}
        className="min-h-[132px] resize-y"
      />
      <Note id={noticeId} className="mt-1.5">
        Do not include confidential invention details.
      </Note>
      <FormEnd
        notSent={notSent}
        ready={ready}
        sending={sending}
        onCancel={onCancel}
      />
    </form>
  );
}

/**
 * Why the last send made no ticket, if it did not, and the form's two acts.
 * Both are held while a send is in flight.
 *
 * Confirm is held with `aria-disabled` rather than `disabled`, so the keyboard
 * that pressed it stays on it: a browser drops focus to the page from a control
 * disabled under it, and the next Tab would land behind the popover. It says
 * it is sending in words, since the spinner beside them says nothing to a
 * screen reader, and a popover that will not close needs a reason on show.
 * At rest it holds "Confirm" alone, so it is as wide as the design draws it;
 * the longer label widens it for the length of a send.
 */
function FormEnd({
  notSent,
  ready,
  sending,
  onCancel,
}: {
  notSent: keyof typeof NOT_SENT | null;
  ready: boolean;
  sending: boolean;
  onCancel: () => void;
}) {
  return (
    <>
      {notSent !== null && (
        <Note tone="destructive" role="alert" className="mt-3">
          {NOT_SENT[notSent]}
        </Note>
      )}
      <div className="mt-3.5 flex justify-end gap-2">
        <Button
          type="button"
          variant="tertiary"
          size="foot"
          disabled={sending}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="foot"
          disabled={!ready}
          aria-disabled={sending || undefined}
        >
          {sending ? (
            <>
              <Spinner size="sm" />
              Sending…
            </>
          ) : (
            "Confirm"
          )}
        </Button>
      </div>
    </>
  );
}
